import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AccountDetail,
  type AccountInput,
  type AccountSummary,
  type ConnectionState,
  type TradingMode,
} from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  cn,
  ConfirmButton,
  KeyValue,
  QueryBoundary,
  Spinner,
} from '../components/ui';
import { useAccounts } from '../hooks/accounts';
import { ago, money } from '../lib/format';
import { atLeast } from '../lib/roles';

const MODES: TradingMode[] = ['backtest', 'paper', 'demo', 'live'];

const EMPTY: AccountInput = {
  name: '',
  broker: null,
  server: null,
  login: null,
  currency: 'USD',
  leverage: null,
  trading_mode: 'paper',
  gateway_url: null,
  magic_number: null,
  trading_day_offset_min: 0,
  is_enabled: false,
};

function modeTone(m: string) {
  return m === 'live' ? 'bad' : m === 'demo' ? 'warn' : 'info';
}

/* ------------------------------------------------------------------- form */

function AccountForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: AccountInput;
  submitLabel: string;
  onSubmit: (value: AccountInput) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [v, setV] = useState<AccountInput>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof AccountInput>(k: K, value: AccountInput[K]) =>
    setV((s) => ({ ...s, [k]: value }));

  const text = (k: keyof AccountInput, label: string, placeholder = '') => (
    <label className="block space-y-1">
      <span className="text-xs text-slate-400">{label}</span>
      <input
        value={(v[k] as string | number | null) ?? ''}
        onChange={(e) => set(k, (e.target.value === '' ? null : e.target.value) as AccountInput[typeof k])}
        placeholder={placeholder}
        className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
      />
    </label>
  );

  const numField = (k: keyof AccountInput, label: string) => (
    <label className="block space-y-1">
      <span className="text-xs text-slate-400">{label}</span>
      <input
        type="number"
        value={(v[k] as number | null) ?? ''}
        onChange={(e) => set(k, (e.target.value === '' ? null : Number(e.target.value)) as AccountInput[typeof k])}
        className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
      />
    </label>
  );

  async function submit() {
    if (!v.name.trim()) {
      setErr('Name is required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ ...v, name: v.name.trim() });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {text('name', 'Name *', 'e.g. IC Markets demo')}
        {text('broker', 'Broker', 'IC Markets')}
        {text('server', 'Server (exact)', 'ICMarketsSC-Demo')}
        {numField('login', 'Login')}
        {text('currency', 'Currency', 'USD')}
        {numField('leverage', 'Leverage')}
        <label className="block space-y-1">
          <span className="text-xs text-slate-400">Trading mode</span>
          <select
            value={v.trading_mode}
            onChange={(e) => set('trading_mode', e.target.value as TradingMode)}
            className="w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
          >
            {MODES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        {numField('magic_number', 'Magic number')}
        {text('gateway_url', 'Gateway URL', 'http://mt5-gateway:8001')}
        {numField('trading_day_offset_min', 'Trading-day offset (min)')}
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={v.is_enabled}
          onChange={(e) => set('is_enabled', e.target.checked)}
        />
        Enabled (the loop will trade this account)
      </label>

      <p className="rounded border border-edge bg-surface px-2 py-1.5 text-xs text-slate-400">
        <strong className="text-slate-300">No password field, by design.</strong> MT5 master
        credentials live <em>only</em> inside the gateway container (ARCHITECTURE §19) — the core
        system never sees them and this table has no password column. Set{' '}
        <code>MT5_LOGIN</code> / <code>MT5_PASSWORD</code> / <code>MT5_SERVER</code> in the
        deployment environment for the <code>mt5-gateway</code> service. The{' '}
        <em>Login</em> and <em>Server</em> above are the identity the pre-flight guard checks the
        connected terminal against — they must match those env values exactly.
      </p>

      {v.trading_mode === 'live' && (
        <p className="rounded border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
          Live mode still needs <code>LIVE_TRADING_ENABLED=true</code> +{' '}
          <code>ENVIRONMENT=production</code> and a matching connected account (INV-4).
        </p>
      )}
      {err && <p className="text-xs text-red-400">{err}</p>}

      <div className="flex gap-2">
        <Button variant="primary" size="sm" disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function toInput(d: AccountDetail): AccountInput {
  return {
    name: d.name,
    broker: d.broker,
    server: d.server,
    login: d.login,
    currency: d.currency,
    leverage: d.leverage,
    trading_mode: d.trading_mode,
    gateway_url: d.gateway_url,
    magic_number: d.magic_number,
    trading_day_offset_min: d.trading_day_offset_min,
    is_enabled: d.is_enabled,
  };
}

function diff(before: AccountInput, after: AccountInput): Partial<AccountInput> {
  const out: Partial<AccountInput> = {};
  for (const k of Object.keys(after) as (keyof AccountInput)[]) {
    if (after[k] !== before[k]) Object.assign(out, { [k]: after[k] });
  }
  return out;
}

function EditForm({ id, onDone }: { id: number; onDone: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['account', id], queryFn: () => api.account(id) });
  const save = useMutation({
    mutationFn: (patch: Partial<AccountInput>) => api.updateAccount(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['account', id] });
      qc.invalidateQueries({ queryKey: ['system-status'] });
      onDone();
    },
  });

  if (q.isLoading) return <Spinner label="Loading…" />;
  if (!q.data) return <p className="text-xs text-red-400">Could not load the account.</p>;
  const base = toInput(q.data);

  return (
    <AccountForm
      initial={base}
      submitLabel="Save changes"
      onCancel={onDone}
      onSubmit={async (value) => {
        const patch = diff(base, value);
        if (Object.keys(patch).length === 0) {
          onDone();
          return;
        }
        await save.mutateAsync(patch);
      }}
    />
  );
}

/* --------------------------------------------------------- connection card */

const STATE_LABEL: Record<ConnectionState, { label: string; tone: 'good' | 'bad' | 'warn' | 'neutral' | 'info' }> = {
  connected: { label: 'Connected', tone: 'good' },
  mismatch: { label: 'Wrong account', tone: 'bad' },
  degraded: { label: 'Degraded', tone: 'warn' },
  down: { label: 'Not connected', tone: 'bad' },
  not_applicable: { label: 'No broker', tone: 'neutral' },
};

function ConnectionCard({ accountId }: { accountId: number }) {
  const q = useQuery({
    queryKey: ['account-connection', accountId],
    queryFn: () => api.accountConnection(accountId),
    refetchInterval: 30_000,
    retry: false,
  });

  if (q.isLoading) {
    return <p className="mt-3 text-xs text-slate-500">Checking the broker connection…</p>;
  }
  if (q.isError) {
    return (
      <p className="mt-3 text-xs text-red-400">
        Could not check the connection: {q.error instanceof Error ? q.error.message : 'unknown error'}
      </p>
    );
  }

  const c = q.data;
  if (!c) return null;
  const s = STATE_LABEL[c.state];
  const border =
    s.tone === 'good'
      ? 'border-emerald-900/60 bg-emerald-950/20'
      : s.tone === 'bad'
        ? 'border-red-900/60 bg-red-950/20'
        : s.tone === 'warn'
          ? 'border-amber-900/60 bg-amber-950/20'
          : 'border-edge bg-surface';

  return (
    <div className={cn('mt-3 rounded border p-3', border)}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge tone={s.tone}>{s.label}</Badge>
        <span className="text-xs text-slate-400">{c.detail}</span>
        <button
          onClick={() => q.refetch()}
          className="ml-auto text-xs text-slate-500 hover:text-slate-300"
        >
          Re-check
        </button>
      </div>

      {c.guard_error && (
        <p className="mb-2 rounded border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
          Pre-flight guard: {c.guard_error}
        </p>
      )}

      {c.state !== 'not_applicable' && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
          <Field label="Gateway" value={c.gateway_status ?? '—'} />
          <Field
            label="Terminal"
            value={c.terminal_connected == null ? '—' : c.terminal_connected ? 'logged in' : 'not logged in'}
            bad={c.terminal_connected === false}
          />
          <Field
            label="Algo trading"
            value={c.algo_trading_enabled == null ? '—' : c.algo_trading_enabled ? 'enabled' : 'DISABLED'}
            bad={c.algo_trading_enabled === false}
          />
          <Field label="Build" value={c.terminal_build ?? '—'} />

          {c.connected_login != null && (
            <>
              <Field
                label="Connected login"
                value={String(c.connected_login)}
                bad={c.login_matches === false}
              />
              <Field label="Expected login" value={c.expected_login ?? 'not set'} />
              <Field label="Broker server" value={c.broker_server ?? '—'} />
              <Field label="Account type" value={c.account_trade_mode ?? '—'} />

              <Field label="Balance" value={money(c.balance, c.currency)} />
              <Field label="Equity" value={money(c.equity, c.currency)} />
              <Field label="Free margin" value={money(c.free_margin, c.currency)} />
              <Field
                label="Open positions"
                value={c.open_positions == null ? '—' : String(c.open_positions)}
              />
            </>
          )}
        </div>
      )}

      <p className="mt-2 text-xs text-slate-600">checked {ago(c.checked_at_utc)}</p>
    </div>
  );
}

function Field({ label, value, bad }: { label: string; value: ReactNode; bad?: boolean }) {
  return (
    <div>
      <div className="text-slate-500">{label}</div>
      <div className={cn('tabular-nums', bad ? 'text-red-400' : 'text-slate-200')}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------- halt controls */

function HaltControls({ account }: { account: AccountSummary }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canHalt = atLeast(user?.role, 'operator');
  const canClear = atLeast(user?.role, 'admin');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['accounts'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['system-status'] });
  };
  const stop = useMutation({
    mutationFn: (reason: string) => api.emergencyStop(account.id, reason),
    onSuccess: invalidate,
  });
  const clear = useMutation({
    mutationFn: (note: string) => api.clearHalt(account.id, note || undefined),
    onSuccess: invalidate,
  });

  if (account.trading_halted) {
    return canClear ? (
      <ConfirmButton
        title={`Clear the halt on #${account.id} ${account.name}?`}
        body="Trading resumes on the next loop iteration. Admin-only — un-halting is the more dangerous direction."
        confirmLabel="Clear halt"
        variant="danger"
        needsReason
        onConfirm={(note) => clear.mutateAsync(note)}
      >
        Clear halt
      </ConfirmButton>
    ) : (
      <span className="text-xs text-slate-500">Clearing a halt requires the admin role.</span>
    );
  }

  return canHalt ? (
    <ConfirmButton
      title={`Emergency stop #${account.id} ${account.name}?`}
      body="The loop stops opening positions immediately. Existing positions keep their SL/TP. Audited, raises a CRITICAL incident."
      confirmLabel="Emergency stop"
      variant="danger"
      needsReason
      onConfirm={(reason) => stop.mutateAsync(reason)}
    >
      Emergency stop
    </ConfirmButton>
  ) : (
    <span className="text-xs text-slate-500">Emergency stop requires the operator role.</span>
  );
}

/* -------------------------------------------------------------------- page */

export default function Accounts() {
  const { user } = useAuth();
  const canEdit = atLeast(user?.role, 'admin');
  const { selectedId, select } = useAccounts();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);

  const create = useMutation({
    mutationFn: (body: AccountInput) => api.createAccount(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['system-status'] });
      setCreating(false);
    },
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      api.updateAccount(id, { is_enabled: enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['system-status'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['system-status'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Accounts"
        description="Broker accounts. The row is the identity the gateway is verified against on startup."
        actions={
          canEdit && !creating ? (
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              New account
            </Button>
          ) : undefined
        }
      />

      {creating && (
        <Card className="mb-4 p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-200">New account</h2>
          <AccountForm
            initial={EMPTY}
            submitLabel="Create account"
            onCancel={() => setCreating(false)}
            onSubmit={(value) => create.mutateAsync(value)}
          />
        </Card>
      )}

      <QueryBoundary q={q} isEmpty={q.data?.items.length === 0} empty={<p className="text-sm text-slate-500">No accounts yet. {canEdit ? 'Create one above.' : 'Ask an admin to add one.'}</p>}>
        {q.data && (
          <div className="space-y-4">
            {q.data.items.map((a) => (
              <Card key={a.id} className="p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-slate-100">
                      #{a.id} {a.name}
                    </h2>
                    <Badge tone={modeTone(a.trading_mode)}>{a.trading_mode}</Badge>
                    {a.is_enabled ? <Badge tone="good">enabled</Badge> : <Badge>disabled</Badge>}
                    {a.trading_halted && <Badge tone="bad">HALTED</Badge>}
                    {selectedId === a.id && <Badge tone="info">selected</Badge>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedId !== a.id && (
                      <Button size="sm" onClick={() => select(a.id)}>
                        Select
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button
                          size="sm"
                          disabled={toggleEnabled.isPending}
                          onClick={() => toggleEnabled.mutate({ id: a.id, enabled: !a.is_enabled })}
                        >
                          {a.is_enabled ? 'Disable' : 'Enable'}
                        </Button>
                        {editing !== a.id && (
                          <Button size="sm" onClick={() => setEditing(a.id)}>
                            Edit
                          </Button>
                        )}
                        <ConfirmButton
                          title={`Delete account #${a.id} ${a.name}?`}
                          body={
                            <>
                              Permanently removes the account row and its symbol mappings/specs.
                              {a.is_enabled && (
                                <> It is <strong>enabled</strong> — disable it first.</>
                              )}{' '}
                              An account that has any positions, executions or risk decisions
                              cannot be deleted: that history would lose the account it belongs
                              to. Disable such an account instead.
                            </>
                          }
                          confirmLabel="Delete account"
                          variant="danger"
                          onConfirm={() => remove.mutateAsync(a.id)}
                        >
                          Delete
                        </ConfirmButton>
                      </>
                    )}
                    <HaltControls account={a} />
                  </div>
                </div>

                {editing === a.id ? (
                  <div className="border-t border-edge pt-4">
                    <EditForm id={a.id} onDone={() => setEditing(null)} />
                  </div>
                ) : (
                  <>
                    <KeyValue
                      rows={[
                        ['Broker', a.broker ?? '—'],
                        ['Server', a.server ?? '—'],
                        ['Login', a.login ?? '—'],
                        ['Currency', a.currency ?? '—'],
                        [
                          'Magic number',
                          a.magic_number ?? (
                            <span className="text-amber-400">
                              not set — the system cannot tell its own trades apart from
                              anything else on the account. Set it before trading.
                            </span>
                          ),
                        ],
                        ...(a.trading_halted
                          ? ([['Halt reason', a.halt_reason ?? '—']] as Array<[string, string]>)
                          : []),
                      ]}
                    />
                    <ConnectionCard accountId={a.id} />
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </QueryBoundary>

      {!canEdit && (
        <p className="mt-3 text-xs text-slate-500">
          Creating or editing an account requires the admin role. Emergency stop is operator; clearing
          a halt is admin.
        </p>
      )}
    </>
  );
}
